// Section 15 pairing and the Section 18 pairing policy. Unpartnered adults aged 16 (or the
// policy's minimum age) to 45 pair with 8% chance per eligible pair per season, weighted by the
// pair's happiness and shared work (config.lifecycle, D-32); partners are never siblings, parents
// or children. Under "player approves" a PairingPrompt goes to the next Decision's eventChoices
// (answer kind `pairing`) instead; approved pairs form then if both are still free.
import { clamp, mean, piecewise } from '../../core/math';
import { ageYears, peopleSorted } from '../../core/queries';
import type { PairingPrompt, Person, PersonId, PhaseContext, Prompt } from '../../core/types';
import { moveIn, type BedBook } from './households';

function eligible(ctx: PhaseContext, p: Person, minAge: number): boolean {
  const years = ageYears(ctx.cfg, p.ageSeasons);
  return (
    p.partnerId === null &&
    years >= minAge &&
    years <= ctx.cfg.lifecycle.pairingMaxAgeYears &&
    p.status.guest === null &&
    p.status.inCustody === null &&
    p.plannedDeparture === null
  );
}

/** Section 15: never siblings (any shared parent), parents or children. */
export function areKin(a: Person, b: Person): boolean {
  return (
    a.parentIds.includes(b.id) ||
    b.parentIds.includes(a.id) ||
    a.childIds.includes(b.id) ||
    b.childIds.includes(a.id) ||
    a.parentIds.some((id) => b.parentIds.includes(id))
  );
}

function compatible(ctx: PhaseContext, a: Person, b: Person): boolean {
  if (ctx.cfg.lifecycle.pairingOppositeSexOnly && a.sex === b.sex) return false;
  return !areKin(a, b);
}

/** Section 15: 8% per eligible pair per season x happiness weight x shared-work weight. */
export function pairChance(ctx: PhaseContext, a: Person, b: Person): number {
  const l = ctx.cfg.lifecycle;
  const happiness = piecewise(l.pairingHappinessCurve, mean([a.happiness, b.happiness]));
  const shared = a.job !== 'rest' && a.job === b.job ? l.pairingSharedWorkMul : 1;
  return clamp(l.pairingChance * happiness * shared, 0, 1);
}

/** Partner two people and give them a household of their own if beds allow. */
export function formPair(ctx: PhaseContext, book: BedBook, aId: PersonId, bId: PersonId): void {
  const a = ctx.draft.people[aId];
  const b = ctx.draft.people[bId];
  if (a === undefined || b === undefined) return;
  a.partnerId = b.id;
  b.partnerId = a.id;
  const home = moveIn(ctx, book, a, b);
  const woman = a.sex === 'female' ? a : b;
  const ids = [a.id, b.id].sort() as [PersonId, PersonId];
  ctx.report.add(
    home === null
      ? { kind: 'pairing', personIds: ids, householdId: woman.householdId, apart: true }
      : { kind: 'pairing', personIds: ids, householdId: home },
  );
}

function pairingPromptId(turn: number, a: PersonId, b: PersonId): string {
  return `pairing:t${turn}:${a}-${b}`;
}

/** Answer last season's pairing prompts (approve forms the pair if both are still free). */
export function answerPairings(ctx: PhaseContext, book: BedBook): void {
  const remaining: Prompt[] = [];
  for (const prompt of ctx.draft.openPrompts) {
    if (prompt.kind !== 'pairing') {
      remaining.push(prompt);
      continue;
    }
    const answer = ctx.answers[prompt.id];
    if (answer?.kind !== 'pairing' || !answer.approve) continue;
    const [aId, bId] = prompt.personIds;
    const a = ctx.draft.people[aId];
    const b = ctx.draft.people[bId];
    if (a === undefined || b === undefined || a.partnerId !== null || b.partnerId !== null)
      continue;
    formPair(ctx, book, aId, bId);
  }
  ctx.draft.openPrompts = remaining;
}

/** Roll new pairs among the eligible (sorted order), or ask the player under "player approves". */
export function rollPairings(ctx: PhaseContext, book: BedBook): void {
  const policy = ctx.state.policies.pairing;
  const minAge = Math.max(
    ctx.cfg.lifecycle.pairingMinAgeYears,
    ctx.cfg.lifecycle.pairingMinAgeByPolicy[policy],
  );
  // Scan the committed state; skip anyone who died or paired earlier in this phase.
  const pool = peopleSorted(ctx.state).filter((p) => {
    if (!eligible(ctx, p, minAge)) return false;
    return ctx.draft.people[p.id]?.partnerId === null;
  });
  const taken = new Set<PersonId>();
  for (let i = 0; i < pool.length; i++) {
    const a = pool[i] as Person;
    if (taken.has(a.id)) continue;
    for (let j = i + 1; j < pool.length; j++) {
      const b = pool[j] as Person;
      if (taken.has(b.id) || !compatible(ctx, a, b)) continue;
      if (!ctx.rng.happened(ctx.rng.chance(pairChance(ctx, a, b)))) continue;
      taken.add(a.id).add(b.id);
      if (policy === 'player-approves') {
        const prompt: PairingPrompt = {
          kind: 'pairing',
          id: pairingPromptId(ctx.season.turn, a.id, b.id),
          turn: ctx.season.turn,
          personIds: [a.id, b.id],
        };
        ctx.draft.openPrompts.push(prompt);
      } else formPair(ctx, book, a.id, b.id);
      break;
    }
  }
}

/** Couples living apart move in together as soon as a building has room for them. */
export function reunite(ctx: PhaseContext, book: BedBook): void {
  for (const snap of peopleSorted(ctx.state)) {
    if (snap.partnerId === null || snap.partnerId < snap.id) continue;
    if (ctx.state.people[snap.partnerId]?.householdId === snap.householdId) continue;
    const p = ctx.draft.people[snap.id];
    const q = ctx.draft.people[snap.partnerId];
    if (p === undefined || q === undefined || q.householdId === p.householdId) continue;
    const home = moveIn(ctx, book, p, q);
    if (home !== null)
      ctx.report.add({ kind: 'moved-in', personIds: [p.id, q.id], householdId: home });
  }
}
